// Import required dependencies
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";

// Import components
import ButtonIcon from "../../../components/ButtonIcon/ButtonIcon";
import Tabs from "../../../components/Tabs/Tabs";
import MerchantDetails from "./MerchantDetails/MerchantDetails";
import Files from "./Files/Files";
import FraudScore from "./FraudScore/FraudScore";
import DropDown from "../../../components/DropDown/DropDown";
import Submission from "./Submission/Submission";
import Activity from "./Activity/Activity";
import EditMobile from "./EditMobile/EditMobile";
import SendMerchantLink from "./SendMerchantLink/SendMerchantLink";
import GenerateDownload from "./GenerateDownload/GenerateDownload";
import DownloadVarSheet from "./DownloadVarSheet/DownloadVarSheet";
import ChangeOwner from "./ChangeOwner/ChangeOwner";
import ConfirmationModal from "./ConfirmationModal/ConfirmationModal";
import EditEmail from "./EditEmail/EditEmail";
import EditStatus from "./EditStatus/EditStatus";
import ArchiveApp from "./ArchiveApp/ArchiveApp";
import AssignProcessor from "./AssignProcessor/AssignProcessor";
import { Copier } from "../../../../components/copier";
import AppStatusDropDown from "../../../components/AppStatusDropDown";
import CopyApplication from "./CopyApplication/Index";
import PaperPlane from "../../../assets/svg/icons/common/PaperPlane";
import AssignMSP from "./AssignMSP";
import EditZoho from "./EditZoho/EditZoho";
import SubmitAdditionalDocuments from "./additional-document";
import EditSubmissionDataModal from "./Metadata/EditSubmissionDataModal/EditSubmissionDataModal";
import { useEditSubmissionData } from "./Metadata/hooks/useEditSubmissionData";
import { Link } from "react-router-dom";
import AppStatusDot from "./AppStatusDot/AppStatusDot";

// Import styles
import style from "../Application.module.scss";

// Import API functions
import {
  getPreappById,
  getMerchantInfo,
  updateProgression,
  getNotes,
  deleteBulkApps,
  cancelMultipleApp,
  cancelSignature,
  resendSignature,
} from "../../../../api";

// Import icons
import CopyIcon from "../../../assets/svg/icons/common/copy";
import CircleArrowLeft from "../../../assets/svg/icons/common/CircleArrowLeft";
import CircleArrowRight from "../../../assets/svg/icons/common/CircleArrowRight";
import CloseIcon24 from "../../../assets/svg/icons/common/CloseIcon24";
import KebabIcon from "../../../assets/svg/icons/common/kebab";

// Import config
import config from "../../../../config";
import { isEntitySuperAdmin } from "../../../../components/helper/helper";

// Import stores
import useCommonStore from "../../../stores/common";
import useApplicationStore from "../../../stores/application";
import useUserStore from "../../../stores/user";
import useDomainStore from "../../../stores/domain";
import AddPricingEquipmentModal from "../../../../components/modals/add-pricing-equipment-modal/add-pricing-equipment-modal";
import ApplicationDetailsSkeleton from "./ApplicationDetailsSkeleton";
import { withRouter } from "react-router-dom";
import EditVARSheet from "./EditVarSheet";
import { useMediaQuery } from "../../../hooks/useMediaQuery";
import { isAppArchived } from "../../../utils/is-app-archived";
import { isAppRetentionExpired } from "../../../utils/is-app-retention-expired";

// Import constants
const MAIN_URL = config.paymentcloud.MAIN_URL;

// Define constants
const awaiting_signature_request = 6;
const app_pending_pc_approval = 15;
const Prevet_Application = 19;
const awaiting_signature = 7;
const hide_progression = [
  awaiting_signature_request,
  app_pending_pc_approval,
  awaiting_signature,
  Prevet_Application,
];

/**
 * ApplicationDetails component for displaying and managing application details
 * @component
 * @returns {JSX.Element} ApplicationDetails component
 */
function ApplicationDetails({ history }) {
  const { isKurvDomain } = useDomainStore();
  const expandDetails = useCommonStore((state) => state.expandDetails);
  const setShowDetails = useCommonStore((state) => state.setShowDetails);
  const toggleExpandDetails = useCommonStore(
    (state) => state.toggleExpandDetails,
  );
  const activeApp = useApplicationStore((state) => state.activeApp);
  const activeAppData = useApplicationStore((state) => state.activeAppData);
  const setActiveAppData = useApplicationStore(
    (state) => state.setActiveAppData,
  );
  const loggedInUser = useUserStore((state) => state.user);
  const isMobile = useMediaQuery(768);
  const isAutoExpanded = useMediaQuery(1200);
  const [loading, setLoading] = useState(true);
  const [showSendMerchantLink, setShowSendMerchantLink] = useState(false);
  const [showGenerateNDownload, setShowGenerateNDownload] = useState(false);
  const [showDownloadVarSheet, setShowDownloadVarSheet] = useState(false);
  const [showChangeOwner, setShowChangeOwner] = useState(false);
  const [openCancelConfirmation, setOpenCancelConfirmation] = useState(false);
  const [openEditEmail, setOpenEditEmail] = useState(false);
  const [openEditMobile, setOpenEditMobile] = useState(false);
  const [openArchiveApp, setOpenArchiveApp] = useState(false);
  const [openAssignProcessor, setOpenAssignProcessor] = useState(false);
  const [confirmCopyApplication, setConfirmCopyApplication] = useState(false);
  const [confirmUpdateProgression, setConfirmUpdateProgression] =
    useState(false);
  const [selectedProgression, setSelectedProgression] = useState(null);
  const [ehawkData, setEhawkData] = useState(null);
  const [confirmAppDelete, setConfirmAppDelete] = useState(false);
  const [assignMspModal, setAssignMspModal] = useState(false);
  const [openAdditionalDocuments, setOpenAdditionalDocuments] = useState(false);
  const [cancelSignatureModal, setCancelSignatureModal] = useState(false);
  const [resendForSignatureModal, setResendForSignatureModal] = useState(false);
  const [openEditStatus, setOpenEditStatus] = useState(false);
  const [editZohoModal, setEditZohoModal] = useState(false);
  const [openPAndEEditing, setOpenPAndEEditing] = useState(false);
  const [zohoIdType, setZohoIdType] = useState("zoho_id");
  const [openEditVARSheetModal, setOpenEditVARSheetModal] = useState(false);
  // Edit Application Metadata functionality
  const {
    isModalOpen,
    currentPowerapp,
    loading: editLoading,
    openModal,
    closeModal,
    handleSave,
  } = useEditSubmissionData();

  // As in ApplicationRow: every endpoint behind the kebab refuses an
  // application past the retention window, so the menu is not offered at all.
  // Falls back to the list row while the detail request is still in flight, so
  // the menu does not flash before it resolves.
  const retentionExpired = isAppRetentionExpired(activeAppData ?? activeApp);

  const isPciAdmin =
    loggedInUser?.role == "admin" && loggedInUser?.organization?.id == 1;
  const isKurvAdmin =
    loggedInUser?.role == "admin" &&
    loggedInUser?.organization?.id == config.paymentcloud.KURVPAY_ORG_ID;

  /** Only show copy icon when the value is present and not the blank placeholder "-" */
  const isCopyable = (val) => {
    const s = val != null ? String(val).trim() : "";
    return s !== "" && s !== "-";
  };

  const formatMerchantLinkOpenedAt = (openedAt) => {
    if (!openedAt) {
      return "No";
    }

    const openedDate = new Date(openedAt);
    if (Number.isNaN(openedDate.getTime())) {
      return "Yes";
    }

    const month = String(openedDate.getMonth() + 1).padStart(2, "0");
    const day = String(openedDate.getDate()).padStart(2, "0");
    const year = openedDate.getFullYear();
    const hours = String(openedDate.getHours()).padStart(2, "0");
    const minutes = String(openedDate.getMinutes()).padStart(2, "0");

    return `Yes (${month}/${day}/${year} ${hours}:${minutes})`;
  };

  // Main tab content component for mobile view
  const MainTabContent = () => {
    return (
      <div className={style.mainTabContent}>
        <div className={style.mainTabList}>
          <div className={style.data_item}>
            <span>App ID</span>
            <div>
              <span>{activeApp?.id}</span>
              <Copier text={activeApp?.id}>
                <CopyIcon />
              </Copier>
            </div>
          </div>

          <div className={style.data_item}>
            <span>Zoho ID</span>
            <div>
              <span>{activeApp?.zoho_id || "-"}</span>
              {activeApp?.zoho_id && (
                <Copier text={activeApp?.zoho_id}>
                  <CopyIcon />
                </Copier>
              )}
            </div>
          </div>

          <div className={style.data_item}>
            <span>Submission ID</span>
            <div>
              <span>{activeApp?.zoho_submission_id || "-"}</span>
              {activeApp?.zoho_submission_id && (
                <Copier text={activeApp?.zoho_submission_id}>
                  <CopyIcon />
                </Copier>
              )}
            </div>
          </div>

          <div className={style.data_item}>
            <span>Merchant Opened Link</span>
            <div>
              <span>
                {formatMerchantLinkOpenedAt(
                  activeAppData?.merchant_link_opened_at,
                )}
              </span>
            </div>
          </div>

          <div className={style.data_item}>
            <span>MID</span>
            <div>
              <span>{activeAppData?.power_app_submission?.mid || "-"}</span>
            </div>
          </div>

          <div className={style.data_item}>
            <span>Merchant Legal</span>
            <div>
              <span>{activeAppData?.field_legalname || "-"}</span>
            </div>
          </div>

          <div className={style.data_item}>
            <span>Bank Name</span>
            <div>
              <span>{activeAppData?.bank?.name || "-"}</span>
              {isCopyable(activeAppData?.bank?.name) && (
                <Copier text={activeAppData?.bank?.name}>
                  <CopyIcon />
                </Copier>
              )}
            </div>
          </div>

          <div className={style.data_item}>
            <span>Organization</span>
            <div>
              <span>
                {activeAppData?.organization?.id ? (
                  <Link
                    to={
                      (loggedInUser?.is_v2_enable == 1 ? "" : "/v2") +
                      "/organizations/" +
                      activeAppData?.organization?.id
                    }
                  >
                    {activeAppData?.organization?.name}
                  </Link>
                ) : (
                  <>{activeAppData?.organization?.name || "-"}</>
                )}
              </span>
              {isCopyable(activeAppData?.organization?.name) && (
                <Copier text={activeAppData?.organization?.name}>
                  <CopyIcon />
                </Copier>
              )}
            </div>
          </div>

          <div className={style.data_item}>
            <span>Org Type</span>
            <div>
              <span>
                {activeAppData?.organization?.type
                  ? activeAppData?.organization?.type == "hands_on"
                    ? "Agent/ISO"
                    : "Referral Partner"
                  : "-"}
              </span>
            </div>
          </div>

          <div className={style.data_item}>
            <span>Agent</span>
            <div>
              <div className={style.name_f_letter}>
                {activeAppData?.user?.name?.[0]}
              </div>
              <p>{activeAppData?.user?.name}</p>
            </div>
          </div>

          <div className={style.data_item}>
            <span>Team Lead</span>
            <div>
              <span>
                {activeAppData?.organization?.get_primary_contact?.[0]?.name ||
                  "-"}
              </span>
            </div>
          </div>

          <div className={style.data_item}>
            <span>Underwriting</span>
            <div>
              <span>{activeAppData?.organization?.uw_data?.name || "-"}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const tabs = [
    {
      label: "Main",
      content: <MainTabContent />,
      badge: null,
      mobileOnly: true,
    },
    {
      label: "Merchant",
      content: <MerchantDetails appData={activeAppData} />,
    },
    {
      label: "Files",
      content: <Files appData={activeAppData} />,
    },
    {
      label: "Fraud Score",
      content: <FraudScore data={ehawkData} />,
      badge: `${ehawkData?.content?.score?.risk !== undefined ? ehawkData?.content?.score?.risk : ""}`,
      hidden: isPciAdmin ? false : true,
    },
    {
      label: "Submissions",
      content: <Submission appData={activeAppData} onSuccess={fetchApplication} />,
      badge: "",
      hidden: activeAppData?.lead_progression_id > 7 ? false : true,
    },
    {
      label: "Activity",
      content: <Activity activeApp={activeApp} />,
    },
  ];

  const editApplication = () => {
    if (isAppArchived(activeAppData?.created_at)) {
      setOpenArchiveApp(true);
      return;
    }
    if (activeAppData?.app_version == 2) {
      history.push(
        `/app/${activeAppData.user.agent_url}/${activeAppData.editslug}`,
      );
    } else {
      let link = "";
      link = `${MAIN_URL}${activeAppData.user.agent_url}/${activeAppData.editslug}`;
      window.open(link);
    }
  };

  const editMetadata = () => {
    if (!activeAppData && !activeAppData.editslug) {
      console.error("No application data available");
      return;
    }

    openModal(activeAppData.editslug);
  };

  const handleSaveEditedData = async (editedData) => {
    const result = await handleSave(editedData);

    if (result.success) {
      // You can add a success notification here
      console.log("Data updated successfully:", result.message);
    } else {
      // You can add an error notification here
      console.error("Error updating data:", result.message);
    }

    return result;
  };

  const copyApplicationURL = () => {
    let url = "";
    if (activeAppData?.app_version == 2) {
      url = `${window.location.origin}/app/${activeAppData.user.agent_url}/${activeAppData.editslug}`;
    } else {
      url = `${MAIN_URL}${activeAppData.user.agent_url}/${activeAppData.editslug}`;
    }
    navigator.clipboard.writeText(url);
    toast.success("Application URL copied to clipboard", {
      duration: 2000,
    });
  };

  const drop_down_options = [
    {
      text: "Download Docs",
      type: "normal",
      hidden: !(activeAppData?.lead_progression_id > 4),
      onClick: () => {
        setShowGenerateNDownload(true);
      },
    },
    {
      text: "Download VAR Sheet",
      type: "normal",
      hidden: !activeAppData?.has_var_sheet,
      onClick: () => {
        setShowDownloadVarSheet(true);
      },
    },
    {
      text: "Copy Application URL",
      type: "normal",
      onClick: () => {
        copyApplicationURL();
      },
    },
    {
      text: "Assign Processor",
      type: "normal",
      onClick: () => {
        setOpenAssignProcessor(true);
      },
    },
    {
      text: "Assign MSP",
      type: "normal",
      onClick: () => {
        setAssignMspModal(true);
      },
    },
    {
      text: "Cancel Signature",
      type: "normal",
      onClick: () => {
        setCancelSignatureModal(true);
      },
      hidden:
        activeAppData?.progression_id == awaiting_signature ? false : true,
    },
    {
      text: "Re-Send For Signature",
      type: "normal",
      onClick: () => {
        setResendForSignatureModal(true);
      },
      hidden:
        activeAppData?.progression_id == awaiting_signature ? false : true,
    },
    {
      text: "Change owner",
      type: "normal",
      onClick: () => {
        setShowChangeOwner(true);
      },
      hidden: !isPciAdmin && !isKurvAdmin,
    },
    {
      text: "Duplicate Application",
      type: "normal",
      onClick: () => {
        setConfirmCopyApplication(true);
      },
    },
    {
      text: "Edit Zoho ID",
      type: "normal",
      onClick: () => {
        handleEditZoho("zoho_id");
      },
      hidden: !isPciAdmin,
    },
    {
      text: "Edit Submission ID",
      type: "normal",
      onClick: () => {
        handleEditZoho("submission_id");
      },
      hidden: !isPciAdmin,
    },
    {
      text: "Edit Application",
      type: "normal",
      onClick: () => {
        editApplication();
      },
      hidden: activeAppData?.editslug?.includes("Imported"),
    },
    {
      text: "Edit Metadata",
      type: "normal",
      onClick: () => {
        editMetadata();
      },
      hidden: !isPciAdmin,
    },
    {
      text: "Edit OTP Email",
      type: "normal",
      onClick: () => {
        setOpenEditEmail(true);
      },
    },
    {
      text: "Edit OTP Mobile",
      type: "normal",
      onClick: () => {
        setOpenEditMobile(true);
      },
    },
    {
      text: "Submit Additional Documents",
      type: "normal",
      onClick: () => {
        setOpenAdditionalDocuments(true);
      },
      hidden: activeAppData?.progression_id < 8,
    },
    {
      text: "Edit Status",
      type: "normal",
      onClick: () => {
        setOpenEditStatus(true);
      },
      hidden: !isPciAdmin,
    },
    {
      text: "Edit VAR Sheet",
      type: "normal",
      onClick: () => {
        setOpenEditVARSheetModal(true);
      },
      hidden: false,
    },
    {
      text: "Cancel",
      type: "danger",
      onClick: () => {
        setOpenCancelConfirmation(true);
      },
    },
    {
      text: "Delete",
      type: "danger",
      onClick: () => {
        setConfirmAppDelete(true);
      },
      hidden: !isPciAdmin,
    },
  ];

  const onCloseClick = () => {
    setShowDetails(false);
    setActiveAppData(null);
    if (expandDetails) {
      toggleExpandDetails();
    }
  };

  const fetchEhawkData = async () => {
    try {
      const notes_response = await getNotes({
        id: activeApp.id,
        parent_id: "",
      });
      if (notes_response?.status && notes_response?.ehawk?.length > 0) {
        setEhawkData(notes_response?.ehawk[0]);
      } else {
        setEhawkData(null);
      }
    } catch (error) {
      console.error("Error fetching ehawk data:", error);
      setEhawkData(null);
    }
  };

  // Hoisted function declaration (not `const`) - the tabs array below
  // references fetchApplication before this point in the component body.
  async function fetchApplication() {
    setLoading(true);
    setActiveAppData(null);
    try {
      if (activeApp?.id) {
        const res = await getPreappById({ id: activeApp.id });
        if (res.status) {
          try {
            const merchant_response = await getMerchantInfo({
              id: activeApp.id,
            });
            if (merchant_response.status) {
              setLoading(false);
              setActiveAppData({
                ...res.data,
                bankinfo: merchant_response.bankinfo,
                creditcardprocess: merchant_response.creditcardprocess,
                powerappbusinessprofile:
                  merchant_response.powerappbusinessprofile,
                powerappequipment: merchant_response.powerappequipment,
                prices: merchant_response.prices,
                signers: merchant_response.signers,
                billing: merchant_response.billing,
                powerappReferral: merchant_response.powerappReferral,
                powerapp_addendum: merchant_response.powerapp_addendum,
                powerapp_plaid_idv: merchant_response.powerapp_plaid_idv,
              });
            } else {
              setLoading(false);
            }
          } catch (error) {
            console.error("Error fetching merchant info:", error);
            setLoading(false);
            toast.error("Failed to load merchant information");
          }
        } else {
          setLoading(false);
        }
      } else {
        console.error("Active app ID is not set");
      }
    } catch (error) {
      console.error("Error fetching application:", error);
      setLoading(false);
      toast.error("Failed to load application data");
    }
  }

  useEffect(() => {
    fetchApplication();
    fetchEhawkData();
  }, [JSON.stringify(activeApp)]);

  const progression_options = [
    {
      value: 1,
      label: "Back to Edit",
      color: "orange",
    },
    {
      value: 8,
      label: "Signed - PC Review",
      color: "#819FEC",
    },
    {
      value: 9,
      label: "Underwriting Review",
      color: "#7FCECE",
    },
    {
      value: 10,
      label: "Pended",
      color: "#E7B8DF",
    },
    {
      value: 11,
      label: "Awaiting Reserve",
      color: "#44AE42",
    },
    {
      value: 12,
      label: "Approved",
      color: "#44AE42",
    },
    {
      value: 13,
      label: "Denied",
      color: "#FFA500",
    },
    {
      value: 14,
      label: "Cancelled",
      color: "#660000",
    },
    {
      value: 15,
      label: "App pending PC approval",
      color: "#44AE42",
    },
    {
      value: 17,
      label: "PDF requested",
      color: "#F0BE74",
    },
    {
      value: 18,
      label: "Awaiting wet signature",
      color: "#F0BE74",
    },
    {
      value: 21,
      label: "Terminated",
      color: "#FF0000",
    },
    {
      value: 22,
      label: "Support",
      color: "#0000FF",
    },
  ];

  const shouldShowProgressionSelect = () => {
    if (!activeAppData || !activeAppData.organization || !activeAppData.user)
      return false;

    // Case 1: Sales Channel Admin showing Sales Progression Select
    if (
      activeAppData.user.channel === "sales_channel" &&
      loggedInUser?.channel === "sales_channel" &&
      loggedInUser?.role === "admin" &&
      activeAppData.lead_progression_id > 7
    ) {
      return true;
    }

    // Case 2: PCI Admin showing General Progression Select
    if (
      isPciAdmin &&
      activeAppData?.progression_id > 7 &&
      activeAppData?.progression_id !== 20 &&
      !hide_progression.includes(activeAppData?.progression_id)
    ) {
      return true;
    }

    return false;
  };

  const updateAppProgression = async () => {
    const data = {
      id: activeAppData?.id,
      progression_id: selectedProgression,
      channel: loggedInUser?.channel,
    };
    const toastId = toast.loading("Updating application status...", {
      duration: 30 * 1000,
    });
    const response = await updateProgression(data);

    if (response.status) {
      toast.success("Status updated successfully.", {
        duration: 10 * 1000,
        id: toastId,
      });
      fetchApplication();
      setConfirmUpdateProgression(false);
      setSelectedProgression(null);
    } else {
      toast.error("Something went wrong!", {
        duration: 10 * 1000,
        id: toastId,
      });
    }
  };

  const deleteApplication = async () => {
    const toastID = toast.loading("Deleting apps...");
    const response = await deleteBulkApps({
      bulkDeleteIds: [activeAppData?.id],
    });
    if (response.status) {
      toast.success("Apps deleted successfully", { id: toastID });
      fetchApplication();
      setConfirmAppDelete(false);
      setShowAppDetails(false);
      setActiveApp(null);
    } else {
      toast.error("Something went wrong", { id: toastID });
    }
  };

  const cancelApplication = async () => {
    const toastID = toast.loading("Cancelling apps...");
    const response = await cancelMultipleApp({ ids: [activeAppData?.id] });
    if (response.status) {
      toast.success("Apps cancelled successfully", { id: toastID });
      setOpenCancelConfirmation(false);
      setShowAppDetails(false);
      setActiveApp(null);
    } else {
      toast.error("Something went wrong", { id: toastID });
    }
  };

  const handleCancelSignature = async () => {
    const toastID = toast.loading("Cancelling signature...");
    const response = await cancelSignature({
      id: activeAppData?.id,
      stratus: true,
    });
    if (response.status) {
      toast.success("Signature cancelled successfully", { id: toastID });
      fetchApplication();
      setCancelSignatureModal(false);
    } else {
      toast.error("Something went wrong", { id: toastID });
    }
  };

  const handleResendForSignature = async () => {
    const toastID = toast.loading("Resending for signature...");
    const response = await resendSignature({
      id: activeAppData?.id,
      stratus: true,
    });
    if (response.status) {
      toast.success("Signature resent successfully", { id: toastID });
      fetchApplication();
      setResendForSignatureModal(false);
    } else {
      toast.error("Something went wrong", { id: toastID });
    }
  };

  const handleEditZoho = (type) => {
    setZohoIdType(type);
    setEditZohoModal(true);
  };

  if (loading) {
    return (
      <div className={style.app_details_container}>
        <ApplicationDetailsSkeleton />
      </div>
    );
  }
  return (
    <div
      className={`${style.app_details_container} ${isKurvDomain ? style.kurv_theme : ""}`}
    >
      {!loading && (
        <>
          {/* Panel Header */}
          <div className={style.details_head}>
            {!expandDetails && (
              <button
                className={style.expand_button}
                onClick={toggleExpandDetails}
                style={{ visibility: isAutoExpanded ? "hidden" : "visible" }}
              >
                <CircleArrowLeft color={isKurvDomain ? "#22A276" : "#254FE2"} />
                <span style={{ color: isKurvDomain ? "#22A276" : "#254fe2" }}>
                  Expand Details
                </span>
              </button>
            )}

            {expandDetails && (
              <button
                className={style.expand_button}
                onClick={toggleExpandDetails}
                style={{ visibility: isAutoExpanded ? "hidden" : "visible" }}
              >
                <CircleArrowRight
                  color={isKurvDomain ? "#22A276" : "#254FE2"}
                />
                <span style={{ color: isKurvDomain ? "#22A276" : "#254fe2" }}>
                  Minimize
                </span>
              </button>
            )}

            <ButtonIcon onClick={onCloseClick}>
              <CloseIcon24 />
            </ButtonIcon>
          </div>

          <div className={style.application_min_data}>
            {/* DBA name and CTAs */}
            <div className={style.container_min_data}>
              <div className={style.left}>
                <span className={style.dba_name}>
                  {activeApp?.field_dba}
                </span>
                <AppStatusDot progression_id={activeAppData?.lead_progression_id} />
              </div>        
              <div className={style.right}>
                {/* Hidden past the retention window with the kebab: the modal
                    fills its recipient list from the application's own emails
                    and its note from get_email_info, both withheld by then, so
                    the form comes up on placeholders and there is no address
                    left to send the link to. */}
                {!retentionExpired && (
                  <ButtonIcon
                    bgColor="#EDEDF4"
                    onClick={() => setShowSendMerchantLink(true)}
                    className={style.send_merchant_link}
                  >
                    <PaperPlane />
                  </ButtonIcon>
                )}
                {/* Hidden past the retention window with the kebab: past the
                    30-day archive window this opens ArchiveApp, whose only
                    action duplicates the application, and /v2/duplicate-app is
                    refused by then — CopyApplication awaits it with no catch,
                    so the 403 leaves its spinner up for good. */}
                {!retentionExpired &&
                  ((activeAppData?.organization_id == 1 &&
                    activeAppData?.lead_progression_id > 3 &&
                    activeAppData?.lead_progression_id < 7) ||
                    (activeAppData?.organization_id != 1 &&
                      activeAppData?.lead_progression_id < 7)) && (
                    <ButtonIcon
                      bgColor="#EDEDF4"
                      className={style.download_button}
                      onClick={() => {
                        if (
                          activeAppData?.created_at &&
                          isAppArchived(activeAppData?.created_at)
                        ) {
                          setOpenArchiveApp(true);
                          return;
                        }
                        if (activeAppData?.app_version == 2) {
                          history.push(`/v2/edit-pricing-equipment/${activeAppData?.editslug}`);
                        } else {
                          setOpenPAndEEditing(true);
                        }
                      }}
                    >
                      <span>Pricing and Equipment</span>
                    </ButtonIcon>
                  )}
                {!retentionExpired && (
                  <DropDown
                    options={drop_down_options}
                    className={style.dropdown}
                  >
                    <KebabIcon />
                  </DropDown>
                )}
              </div>
            </div>

            {/* App primary details */}
            <div
              className={`${style.app_details} ${expandDetails ? style.expanded : ""}`}
            >
              <div className={style.data_item}>
                <span>App ID</span>
                <div>
                  <span>{activeApp.id}</span>
                  <Copier text={activeApp.id}>
                    <CopyIcon />
                  </Copier>
                </div>
              </div>

              <div className={style.data_item}>
                <span>Zoho ID</span>
                <div>
                  <span>{activeApp.zoho_id || "-"}</span>
                  <div className={style.actions}>
                    {activeApp.zoho_id && (
                      <Copier text={activeApp.zoho_id}>
                        <CopyIcon />
                      </Copier>
                    )}
                  </div>
                </div>
              </div>

              <div className={style.data_item}>
                <span>Submission ID</span>
                <div>
                  <span>{activeApp.zoho_submission_id || "-"}</span>
                  <div className={style.actions}>
                    {activeApp.zoho_submission_id && (
                      <Copier text={activeApp.zoho_submission_id}>
                        <CopyIcon />
                      </Copier>
                    )}
                  </div>
                </div>
              </div>

              <div className={style.data_item}>
                <span>Merchant Opened Link</span>
                <div>
                  <span>
                    {formatMerchantLinkOpenedAt(
                      activeAppData?.merchant_link_opened_at,
                    )}
                  </span>
                </div>
              </div>

              {expandDetails && (
                <>
                  <div className={style.data_item}>
                    <span>MID</span>
                    <div>
                      <span>
                        {activeAppData?.power_app_submission?.mid || "-"}
                      </span>
                      {isCopyable(activeAppData?.power_app_submission?.mid) && (
                        <Copier text={activeAppData?.power_app_submission?.mid}>
                          <CopyIcon />
                        </Copier>
                      )}
                    </div>
                  </div>

                  <div className={style.data_item}>
                    <span>Merchant Legal</span>
                    <div>
                      <span>{activeAppData?.field_legalname || "-"}</span>
                      {isCopyable(activeAppData?.field_legalname) && (
                        <Copier text={activeAppData?.field_legalname}>
                          <CopyIcon />
                        </Copier>
                      )}
                    </div>
                  </div>
                </>
              )}

              <div className={style.data_item}>
                <span>Bank Name</span>
                <div>
                  <span>{activeAppData?.bank?.name || "-"}</span>
                  {isCopyable(activeAppData?.bank?.name) && (
                    <Copier text={activeAppData?.bank?.name}>
                      <CopyIcon />
                    </Copier>
                  )}
                </div>
              </div>

              <div className={style.data_item}>
                <span>Organization</span>
                <div>
                  <span>
                    {activeAppData?.organization?.id ? (
                      <>
                        <Link
                          to={
                            "/organizations/" + activeAppData?.organization?.id
                          }
                        >
                          {activeAppData?.organization?.name}
                        </Link>
                      </>
                    ) : (
                      <>{activeAppData?.organization?.name || "-"}</>
                    )}
                  </span>
                  {isCopyable(activeAppData?.organization?.name) && (
                    <Copier text={activeAppData?.organization?.name}>
                      <CopyIcon />
                    </Copier>
                  )}
                </div>
              </div>

              {expandDetails && (
                <div className={style.data_item}>
                  <span>Org Type</span>
                  <div>
                    <span>
                      {activeAppData?.organization?.type
                        ? activeAppData?.organization?.type == "hands_on"
                          ? "Agent/ISO"
                          : "Referral Partner"
                        : "-"}{" "}
                    </span>
                    {isCopyable(
                      activeAppData?.organization?.type
                        ? activeAppData?.organization?.type == "hands_on"
                          ? "Agent/ISO"
                          : "Referral Partner"
                        : null,
                    ) && (
                      <Copier
                        text={
                          activeAppData?.organization?.type == "hands_on"
                            ? "Agent/ISO"
                            : "Referral Partner"
                        }
                      >
                        <CopyIcon />
                      </Copier>
                    )}
                  </div>
                </div>
              )}

              <div className={style.data_item}>
                <span>Agent</span>
                <div>
                  <div className={style.name_f_letter}>
                    {activeAppData?.user?.name?.[0]}
                  </div>
                  <p>{activeAppData?.user?.name}</p>
                </div>
              </div>

              {expandDetails && (
                <>
                  <div className={style.data_item}>
                    <span>Team Lead</span>
                    <div>
                      <span>
                        {activeAppData?.organization?.get_primary_contact?.[0]
                          ?.name || "-"}
                      </span>
                      {isCopyable(
                        activeAppData?.organization?.get_primary_contact?.[0]
                          ?.name,
                      ) && (
                        <Copier
                          text={
                            activeAppData?.organization
                              ?.get_primary_contact?.[0]?.name
                          }
                        >
                          <CopyIcon />
                        </Copier>
                      )}
                    </div>
                  </div>

                  <div className={style.data_item}>
                    <span>Underwriting</span>
                    <div>
                      <span>
                        {activeAppData?.organization?.uw_data?.name || "-"}
                      </span>
                      {isCopyable(
                        activeAppData?.organization?.uw_data?.name,
                      ) && (
                        <Copier
                          text={activeAppData?.organization?.uw_data?.name}
                        >
                          <CopyIcon />
                        </Copier>
                      )}
                    </div>
                  </div>

                  <div className={style.data_item}>
                    <span>Kurv Office</span>
                    <div>
                      <span>
                        {activeAppData?.ems_office?.office_id
                          ? activeAppData?.ems_office?.office_id
                          : activeAppData?.user?.ems_office
                            ? activeAppData?.user?.ems_office[0]?.label
                            : "-"}
                      </span>
                      {isCopyable(
                        activeAppData?.ems_office?.office_id
                          ? activeAppData?.ems_office?.office_id
                          : activeAppData?.user?.ems_office?.[0]?.label,
                      ) && (
                        <Copier
                          text={
                            activeAppData?.ems_office?.office_id
                              ? activeAppData?.ems_office?.office_id
                              : activeAppData?.user?.ems_office?.[0]?.label
                          }
                        >
                          <CopyIcon />
                        </Copier>
                      )}
                    </div>
                  </div>

                  <div className={style.data_item}>
                    <span>Kurv Rep ID</span>
                    <div>
                      <span>{activeAppData?.user?.ems_rep_id || "-"}</span>
                      {isCopyable(activeAppData?.user?.ems_rep_id) && (
                        <Copier text={activeAppData?.user?.ems_rep_id}>
                          <CopyIcon />
                        </Copier>
                      )}
                    </div>
                  </div>

                  <div className={style.data_item}>
                    <span>Kurv Branch</span>
                    <div>
                      <span>{activeAppData?.user?.ems_branch || "-"}</span>
                      {isCopyable(activeAppData?.user?.ems_branch) && (
                        <Copier text={activeAppData?.user?.ems_branch}>
                          <CopyIcon />
                        </Copier>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
            {/* Tabs */}
            <div>
              <Tabs tabs={tabs} active_index={isMobile ? 0 : 1} isMobile />
            </div>
          </div>
          {/* Modals */}
          {showSendMerchantLink && (
            <SendMerchantLink
              open={showSendMerchantLink}
              onClose={() => setShowSendMerchantLink(false)}
            />
          )}
          {showGenerateNDownload && (
            <GenerateDownload
              open={showGenerateNDownload}
              onClose={() => setShowGenerateNDownload(false)}
            />
          )}
          {showDownloadVarSheet && (
            <DownloadVarSheet
              open={showDownloadVarSheet}
              onClose={() => setShowDownloadVarSheet(false)}
            />
          )}
          {showChangeOwner && (
            <ChangeOwner
              open={showChangeOwner}
              onClose={() => setShowChangeOwner(false)}
              fetchApplication={fetchApplication}
            />
          )}
          {openCancelConfirmation && (
            <ConfirmationModal
              open={openCancelConfirmation}
              onClose={() => setOpenCancelConfirmation(false)}
              message={
                "Are you sure you want to cancel this app? You can't undo this action."
              }
              onConfirmClick={() => cancelApplication()}
            />
          )}
          {openEditEmail && (
            <EditEmail
              open={openEditEmail}
              onClose={() => setOpenEditEmail(false)}
              appData={activeAppData}
              fetchApplication={fetchApplication}
            />
          )}
          {openEditMobile && (
            <EditMobile
              open={openEditMobile}
              onClose={() => setOpenEditMobile(false)}
              appData={activeAppData}
              fetchApplication={fetchApplication}
            />
          )}
          {/* Edit status */}
          {openEditStatus && (
            <EditStatus
              open={openEditStatus}
              onClose={() => setOpenEditStatus(false)}
              appData={activeAppData}
              fetchApplication={fetchApplication}
            />
          )}
          {/* Edit Application Archive popup */}
          {openArchiveApp && (
            <ArchiveApp
              open={openArchiveApp}
              onClose={() => setOpenArchiveApp(false)}
              onDuplicate={() => {
                setOpenArchiveApp(false);
                setConfirmCopyApplication(true);
              }}
            />
          )}
          {openAssignProcessor && (
            <AssignProcessor
              open={openAssignProcessor}
              onClose={() => setOpenAssignProcessor(false)}
            />
          )}
          {/* Duplicate confirmation */}
          {confirmCopyApplication && (
            <CopyApplication
              appData={activeAppData}
              isOpen={confirmCopyApplication}
              onClose={() => setConfirmCopyApplication(false)}
              loggedInUser={loggedInUser}
              history={history}
            />
          )}
          {/* Progression confirmation */}
          {confirmUpdateProgression && (
            <ConfirmationModal
              open={confirmUpdateProgression}
              onClose={() => setConfirmUpdateProgression(false)}
              message={
                "Are you sure! you want to update progression for selected app(s)?"
              }
              onConfirmClick={() => updateAppProgression()}
            />
          )}
          {/* delete confirmation */}
          {confirmAppDelete && (
            <ConfirmationModal
              open={confirmAppDelete}
              onClose={() => setConfirmAppDelete(false)}
              message={
                "Are you sure you want to Delete this app? You can't undo this action."
              }
              onConfirmClick={() => deleteApplication()}
            />
          )}
          {/* Assign MSP */}
          {assignMspModal && (
            <AssignMSP
              open={assignMspModal}
              onClose={() => setAssignMspModal(false)}
              appData={activeAppData}
              onSuccess={fetchApplication}
            />
          )}
          {cancelSignatureModal && (
            <ConfirmationModal
              open={cancelSignatureModal}
              onClose={() => setCancelSignatureModal(false)}
              message={
                "Canceling signature process will reset everything and return you to the editing. Are you sure you want Cancel signature ?"
              }
              onConfirmClick={() => handleCancelSignature()}
            />
          )}
          {resendForSignatureModal && (
            <ConfirmationModal
              open={resendForSignatureModal}
              onClose={() => setResendForSignatureModal(false)}
              message={"Are you sure you want to resend signature?"}
              onConfirmClick={() => handleResendForSignature()}
            />
          )}
          {editZohoModal && (
            <EditZoho
              open={editZohoModal}
              onClose={() => setEditZohoModal(false)}
              appData={activeAppData}
              getAppData={fetchApplication}
              zohoIdType={zohoIdType}
            />
          )}
          {openPAndEEditing && (
            <AddPricingEquipmentModal
              user={loggedInUser}
              slug={activeAppData.editslug}
              appId={activeAppData.id}
              isOpen={openPAndEEditing}
              close={() => setOpenPAndEEditing(false)}
            />
          )}
          {/* Submit Additional Documents to EMS */}
          {openAdditionalDocuments && (
            <SubmitAdditionalDocuments
              open={openAdditionalDocuments}
              onClose={() => setOpenAdditionalDocuments(false)}
              appData={activeAppData}
            />
          )}
          {/* Edit Application Metadata */}
          {isModalOpen && (
            <EditSubmissionDataModal
              isOpen={isModalOpen}
              onClose={closeModal}
              powerapp={currentPowerapp}
              onSave={handleSaveEditedData}
            />
          )}
          {openEditVARSheetModal && (
            <EditVARSheet
              isOpen={openEditVARSheetModal}
              onClose={() => setOpenEditVARSheetModal(false)}
              powerapp_id={activeAppData.id}
            />
          )}
        </>
      )}
    </div>
  );
}

export default withRouter(ApplicationDetails);
